/* eslint-disable no-undef */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Select, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { ExclamationCircleOutlined, PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { ChevronDown } from 'lucide-react';
import { useCascadingOptions } from '../hooks/useCascadingOptions';
import { RiskCalculatorService } from '@client/services/riskCalculatorService';
import { useIsMobile } from '@client/hooks/useIsMobile';
import { MobileSelectSheet } from '@client/components/mobile';

/**
 * Unified Cascading Select Component
 *
 * Features:
 * - Fetches options based on step and previous selections
 * - Auto-retries on server errors with exponential backoff
 * - Shows subtle warning when value not in filtered options
 * - "Show All" toggle with debounced API search (Ant Design pattern)
 * - Infinite scroll pagination in show all mode
 */
const UnifiedCascadingSelect = ({
  type,
  cultures = [],
  treatmentType = null,
  applicationTechnique = null,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  allowClear = true,
  enrichedLabel = null,
  onMismatchChange,
  allowShowAll = false,
  ...otherProps
}) => {
  const { t } = useTranslation('riskCalculator');
  const isMobile = useIsMobile();

  // State
  const [showAllMode, setShowAllMode] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const prevMismatchRef = useRef(null);

  // Show All mode state (following Ant Design debounce pattern)
  const [showAllOptions, setShowAllOptions] = useState([]);
  const [showAllFetching, setShowAllFetching] = useState(false);
  const [showAllPagination, setShowAllPagination] = useState({ page: 1, hasMore: false });
  const [currentSearch, setCurrentSearch] = useState('');
  const fetchRef = useRef(0);

  // Product formatter
  const formatOption = useCallback((opt) => {
    if (type === 'products') {
      return {
        label: opt.approval_number ? `${opt.name} (${opt.approval_number})` : opt.name,
        value: opt.id,
        count: opt.count,
        doses: opt.doses || [],
        approval_number: opt.approval_number || '',
        name: opt.name,
        rawData: opt,
      };
    }
    return {
      label: opt.name || opt.french_name || opt.english_name || opt.id,
      value: opt.id,
      count: opt.count,
    };
  }, [type]);

  // Fetch aggregated options (normal mode)
  const {
    options: aggregatedOptions,
    loading: aggregatedLoading,
    error: aggregatedError,
    hasNoResults: aggregatedHasNoResults,
    retry,
  } = useCascadingOptions({
    step: type,
    cultures,
    treatmentType,
    applicationTechnique,
    enabled: !disabled && !showAllMode,
    showAll: false,
    formatOption: type === 'products' ? formatOption : undefined,
  });

  // Debounced fetcher for show all mode (Ant Design pattern)
  const debouncedFetcher = useMemo(() => {
    const loadOptions = (searchValue) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      setShowAllFetching(true);
      setCurrentSearch(searchValue);

      RiskCalculatorService.getAllOptions({
        step: type,
        search: searchValue,
        page: 1,
      })
        .then((result) => {
          // Ignore stale responses
          if (fetchId !== fetchRef.current) return;

          const formattedOptions = (result.options || []).map(formatOption);
          setShowAllOptions(formattedOptions);
          setShowAllPagination({ page: 1, hasMore: result.hasMore || false });
          setShowAllFetching(false);
        })
        .catch(() => {
          if (fetchId !== fetchRef.current) return;
          setShowAllFetching(false);
        });
    };

    return debounce(loadOptions, 300);
  }, [type, formatOption]);

  // Load initial options when entering show all mode
  useEffect(() => {
    if (showAllMode && showAllOptions.length === 0 && !showAllFetching) {
      debouncedFetcher('');
    }
  }, [showAllMode, showAllOptions.length, showAllFetching, debouncedFetcher]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetcher.cancel();
    };
  }, [debouncedFetcher]);

  // Reset showAllMode when dependencies change
  useEffect(() => {
    setShowAllMode(false);
    setShowAllOptions([]);
    setShowAllPagination({ page: 1, hasMore: false });
    setCurrentSearch('');
  }, [cultures.join(','), treatmentType, applicationTechnique]);

  // Load more for infinite scroll
  const loadMore = useCallback(() => {
    if (showAllFetching || !showAllPagination.hasMore) return;

    const nextPage = showAllPagination.page + 1;
    fetchRef.current += 1;
    const fetchId = fetchRef.current;

    setShowAllFetching(true);

    RiskCalculatorService.getAllOptions({
      step: type,
      search: currentSearch,
      page: nextPage,
    })
      .then((result) => {
        if (fetchId !== fetchRef.current) return;

        const formattedOptions = (result.options || []).map(formatOption);
        setShowAllOptions((prev) => {
          const existingIds = new Set(prev.map((o) => o.value));
          const newOptions = formattedOptions.filter((o) => !existingIds.has(o.value));
          return [...prev, ...newOptions];
        });
        setShowAllPagination({ page: nextPage, hasMore: result.hasMore || false });
        setShowAllFetching(false);
      })
      .catch(() => {
        if (fetchId !== fetchRef.current) return;
        setShowAllFetching(false);
      });
  }, [type, currentSearch, showAllPagination, showAllFetching, formatOption]);

  // Select the right options based on mode
  const options = showAllMode ? showAllOptions : aggregatedOptions;
  const loading = showAllMode ? (showAllFetching && showAllOptions.length === 0) : aggregatedLoading;
  const isFetching = showAllMode ? showAllFetching : false;
  const error = showAllMode ? null : aggregatedError;
  const hasNoResults = showAllMode
    ? (!showAllFetching && showAllOptions.length === 0)
    : aggregatedHasNoResults;

  // Toggle labels based on type
  const toggleLabels = useMemo(() => ({
    treatment_type: {
      showAll: t('show_all_treatment_types', 'Voir tous les types de traitement'),
      showFiltered: t('show_filtered_treatment_types', 'Voir les types correspondants aux critères'),
    },
    application_technique: {
      showAll: t('show_all_techniques', "Voir toutes les techniques d'application"),
      showFiltered: t('show_filtered_techniques', 'Voir les techniques correspondantes aux critères'),
    },
    products: {
      showAll: t('show_all_products', 'Voir tous les produits'),
      showFiltered: t('show_filtered_products', 'Voir les produits correspondants aux critères'),
    },
    units: {
      showAll: t('show_all_units', 'Voir toutes les unités'),
      showFiltered: t('show_filtered_units', 'Voir les unités correspondantes aux critères'),
    },
  }), [t]);

  const toggleLabel = showAllMode
    ? toggleLabels[type]?.showFiltered
    : toggleLabels[type]?.showAll;

  // Handle toggle - keep dropdown open
  const handleToggle = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowAllMode((prev) => !prev);
    setDropdownOpen(true);
  }, []);

  // Mobile-specific toggle (no event needed)
  const handleMobileToggle = useCallback(() => {
    setShowAllMode((prev) => !prev);
  }, []);

  // Handle selection change
  const handleChange = useCallback((newValue, option) => {
    onChange?.(newValue, option);
    setDropdownOpen(false);
  }, [onChange]);

  // Handle dropdown visibility
  const handleDropdownVisibleChange = useCallback((visible) => {
    setDropdownOpen(visible);
    // Reset search state when closing
    if (!visible && showAllMode) {
      setCurrentSearch('');
    }
  }, [showAllMode]);

  // Handle search (only for show all mode)
  const handleSearch = useCallback((searchValue) => {
    if (showAllMode) {
      debouncedFetcher(searchValue);
    }
  }, [showAllMode, debouncedFetcher]);

  // Handle infinite scroll
  const handlePopupScroll = useCallback((e) => {
    if (!showAllMode || !showAllPagination.hasMore || showAllFetching) return;

    const { target } = e;
    const scrollThreshold = 50;
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - scrollThreshold;

    if (isNearBottom) {
      loadMore();
    }
  }, [showAllMode, showAllPagination.hasMore, showAllFetching, loadMore]);

  // Get selected label for display
  const getSelectedLabel = useCallback(() => {
    if (!value) return '';
    const selectedOption = options.find((opt) => opt.value === value);
    return selectedOption?.label || enrichedLabel || '';
  }, [value, options, enrichedLabel]);

  // Label render with truncation
  const labelRender = useCallback((props) => {
    const { label: propLabel, value: propValue } = props;
    const optionExists = options.some(opt => opt.value === propValue);
    const fullLabel = optionExists
      ? (propLabel || getSelectedLabel())
      : (enrichedLabel || propLabel || getSelectedLabel());

    return (
      <Tooltip title={fullLabel} placement="topLeft">
        <span style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {fullLabel}
        </span>
      </Tooltip>
    );
  }, [getSelectedLabel, options, enrichedLabel]);

  // Status message logic
  const valueNotInOptions = value && !loading && !options.some(opt => opt.value === value);

  // Type name mapping for hint message
  const typeHintNames = useMemo(() => ({
    treatment_type: t('cascading_empty_hint_treatment_type'),
    application_technique: t('cascading_empty_hint_application_technique'),
    products: t('cascading_empty_hint_products'),
    units: t('cascading_empty_hint_units'),
  }), [t]);

  const statusMessage = useMemo(() => {
    if (error) {
      return {
        type: 'error',
        text: t('cascading_error'),
        tooltip: t('cascading_error_tooltip'),
        canRetry: true,
      };
    }

    if (valueNotInOptions && !hasNoResults) {
      return {
        type: 'mismatch',
        text: t(`cascading_mismatch_${type}`, t('cascading_mismatch_default')),
        tooltip: t('cascading_mismatch_tooltip'),
        canRetry: false,
      };
    }

    if (hasNoResults && !loading && !showAllMode) {
      return {
        type: 'empty',
        text: t(`cascading_empty_${type}`, t('cascading_empty_default')),
        tooltip: t('cascading_empty_tooltip'),
        hint: allowShowAll ? t('cascading_empty_hint', { type: typeHintNames[type] }) : null,
        canRetry: false,
      };
    }

    return null;
  }, [error, valueNotInOptions, hasNoResults, loading, type, t, showAllMode, allowShowAll, typeHintNames]);

  // Report mismatch to parent
  useEffect(() => {
    if (!onMismatchChange) return;
    const hasMismatch = statusMessage?.type === 'mismatch' || statusMessage?.type === 'empty';
    const mismatchKey = hasMismatch ? statusMessage.text : null;

    if (prevMismatchRef.current !== mismatchKey) {
      prevMismatchRef.current = mismatchKey;
      onMismatchChange(hasMismatch, mismatchKey);
    }
  }, [statusMessage, onMismatchChange]);

  // Product option render (desktop - uses option.data for Ant Design)
  const productOptionRender = useCallback((option) => (
    <div>
      <div style={{ fontWeight: 500 }}>{option.label}</div>
      <div style={{ fontSize: 12, color: '#8C8C8C', marginTop: 2 }}>
        {option.data?.doses?.length > 0
          ? `Doses SAgE: ${option.data.doses.join(', ')}`
          : t('products_no_dosage')}
      </div>
    </div>
  ), [t]);

  // Product option render for mobile (direct option access)
  const mobileProductOptionRender = useCallback((option) => (
    <div className="mobile-option-content">
      <div className="mobile-option-label" style={{ fontWeight: 500 }}>{option.label}</div>
      <div className="mobile-option-description">
        {option.doses?.length > 0
          ? `Doses SAgE: ${option.doses.join(', ')}`
          : t('products_no_dosage')}
      </div>
    </div>
  ), [t]);

  // Client-side filter for normal mode
  const getFilterOption = useCallback(() => {
    if (showAllMode) return false; // API handles filtering

    if (type === 'products') {
      return (input, option) => {
        const searchTerm = input.toLowerCase();
        const name = (option?.name ?? '').toLowerCase();
        const approvalNumber = (option?.approval_number ?? '').toLowerCase();
        return name.includes(searchTerm) || approvalNumber.includes(searchTerm);
      };
    }

    return (input, option) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  }, [showAllMode, type]);

  // Build select props based on type
  const selectProps = useMemo(() => {
    const baseProps = {
      value,
      onChange: handleChange,
      placeholder: statusMessage?.text || placeholder,
      loading: loading || isFetching,
      disabled: disabled || loading,
      allowClear: allowClear && type !== 'units',
      showSearch: true,
      filterOption: getFilterOption(),
      onSearch: handleSearch,
      notFoundContent: (loading || isFetching) ? <Spin size="small" /> : t('cascading_no_result'),
      options,
      style: { width: '100%', minHeight: 44 },
      labelRender,
      open: dropdownOpen,
      onDropdownVisibleChange: handleDropdownVisibleChange,
      onPopupScroll: handlePopupScroll,
      status: statusMessage?.type === 'error' ? 'error' : undefined,
      ...otherProps,
    };

    if (type === 'products') {
      return {
        ...baseProps,
        optionRender: productOptionRender,
      };
    }

    if (type === 'units') {
      return {
        ...baseProps,
        allowClear: false,
        optionRender: (option) => <div style={{ width: '100%' }}>{option.label}</div>,
        style: { width: '100%', height: '44px' },
      };
    }

    return baseProps;
  }, [
    value, handleChange, placeholder, loading, isFetching, disabled, allowClear,
    type, options, labelRender, dropdownOpen, handleDropdownVisibleChange,
    handlePopupScroll, handleSearch, getFilterOption, statusMessage,
    productOptionRender, otherProps
  ]);

  // Dropdown footer with toggle button and loading indicator
  const dropdownRender = useCallback((menu) => (
    <>
      {menu}
      {/* Loading indicator for infinite scroll */}
      {showAllMode && isFetching && showAllOptions.length > 0 && (
        <div style={{ padding: '8px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
          <Spin size="small" />
        </div>
      )}
      {/* Toggle button */}
      {allowShowAll && (
        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#105235',
            fontSize: 13,
            fontWeight: 500,
            backgroundColor: '#fafafa',
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={handleToggle}
        >
          {showAllMode ? (
            <MinusCircleOutlined style={{ fontSize: 16 }} />
          ) : (
            <PlusCircleOutlined style={{ fontSize: 16 }} />
          )}
          <span>{toggleLabel}</span>
        </div>
      )}
    </>
  ), [showAllMode, isFetching, showAllOptions.length, allowShowAll, handleToggle, toggleLabel]);

  // Get display value for mobile trigger
  const displayValue = useMemo(() => {
    if (!value) return '';
    const selectedOption = options.find((opt) => opt.value === value);
    return selectedOption?.label || enrichedLabel || '';
  }, [value, options, enrichedLabel]);

  // Mobile trigger placeholder
  const triggerPlaceholder = statusMessage?.text || placeholder;

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full">
        {label && (
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            {label}
          </label>
        )}

        <div className="relative">
          {/* Mobile trigger - styled like a select */}
          <div
            onClick={() => !disabled && setSheetOpen(true)}
            style={{
              width: '100%',
              minHeight: 44,
              padding: '8px 12px',
              border: `1px solid ${statusMessage?.type === 'error' ? '#ff4d4f' : '#d9d9d9'}`,
              borderRadius: 6,
              backgroundColor: disabled ? '#f5f5f5' : '#fff',
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: displayValue ? '#262626' : '#bfbfbf',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {displayValue || triggerPlaceholder}
            </span>
            {loading ? (
              <Spin size="small" style={{ marginLeft: 8 }} />
            ) : (
              <ChevronDown size={16} style={{ marginLeft: 8, color: '#bfbfbf', flexShrink: 0 }} />
            )}
          </div>

          {/* Mobile sheet */}
          <MobileSelectSheet
            isOpen={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={label || placeholder || t('select_option', 'Sélectionner')}
            options={options}
            value={value}
            onChange={handleChange}
            loading={loading || isFetching}
            onSearch={showAllMode ? handleSearch : undefined}
            onLoadMore={showAllMode ? loadMore : undefined}
            hasMore={showAllPagination.hasMore}
            optionRender={type === 'products' ? mobileProductOptionRender : undefined}
            showAllToggle={allowShowAll ? {
              enabled: true,
              label: toggleLabel,
              active: showAllMode,
              onClick: handleMobileToggle,
            } : undefined}
            placeholder={t('search_placeholder', 'Rechercher...')}
          />

          {/* Status message */}
          {statusMessage && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 2,
              }}
            >
              <div
                className="flex items-center gap-1"
                style={{
                  fontSize: 11,
                  color: statusMessage.type === 'error' ? '#ff4d4f' : '#d48806',
                  fontStyle: 'italic',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 11, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {statusMessage.text}
                </span>
                {statusMessage.canRetry && (
                  <button
                    onClick={retry}
                    style={{
                      marginLeft: 4,
                      padding: '0 6px',
                      fontSize: 10,
                      border: 'none',
                      background: 'none',
                      color: '#1677ff',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      flexShrink: 0,
                    }}
                  >
                    {t('cascading_retry')}
                  </button>
                )}
              </div>
              {statusMessage.hint && (
                <div
                  style={{
                    fontSize: 10,
                    color: '#595959',
                    fontStyle: 'italic',
                    marginTop: 2,
                  }}
                >
                  {statusMessage.hint}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop render (unchanged)
  return (
    <div className="w-full">
      {label && (
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          {label}
        </label>
      )}

      <div className="relative">
        <Select
          {...selectProps}
          dropdownRender={allowShowAll ? dropdownRender : undefined}
          popupMatchSelectWidth
        />

        {/* Status message */}
        {statusMessage && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 2,
            }}
          >
            <div
              className="flex items-center gap-1"
              style={{
                fontSize: 11,
                color: statusMessage.type === 'error' ? '#ff4d4f' : '#d48806',
                fontStyle: 'italic',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <ExclamationCircleOutlined style={{ fontSize: 11, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {statusMessage.text}
              </span>
              {statusMessage.canRetry && (
                <button
                  onClick={retry}
                  style={{
                    marginLeft: 4,
                    padding: '0 6px',
                    fontSize: 10,
                    border: 'none',
                    background: 'none',
                    color: '#1677ff',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    flexShrink: 0,
                  }}
                >
                  {t('cascading_retry')}
                </button>
              )}
            </div>
            {statusMessage.hint && (
              <div
                style={{
                  fontSize: 10,
                  color: '#595959',
                  fontStyle: 'italic',
                  marginTop: 2,
                }}
              >
                {statusMessage.hint}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

UnifiedCascadingSelect.propTypes = {
  type: PropTypes.oneOf(['treatment_type', 'application_technique', 'products', 'units']).isRequired,
  cultures: PropTypes.arrayOf(PropTypes.string),
  treatmentType: PropTypes.string,
  applicationTechnique: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  enrichedLabel: PropTypes.string,
  onMismatchChange: PropTypes.func,
  allowShowAll: PropTypes.bool,
};

export default React.memo(UnifiedCascadingSelect);
